/**
 * 관리자 알림: 🔔 벨(안 읽은 제출 묶음) + 오른쪽 아래 팝업 + 브라우저 탭 제목 "(n)" + 메뉴 배지용 대기 장수
 * 30초마다 확인
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Box, Button, IconButton, Paper, Popover, Slide, Snackbar, Typography } from '@mui/material';
import { NotificationsNone } from '@mui/icons-material';
import { api, Notifications } from '../api';
import { C, mdhm } from '../common';

export const useCertNotifications = (enabled: boolean) => {
  const [data, setData] = useState<Notifications | null>(null);
  const refresh = useCallback(() => {
    if (!enabled) return;
    api<{ data: Notifications }>('/admin/cert/notifications').then((r) => setData(r.data)).catch(() => undefined);
  }, [enabled]);
  useEffect(() => {
    refresh();
    if (!enabled) return undefined;
    const t = window.setInterval(refresh, 30000);
    return () => window.clearInterval(t);
  }, [enabled, refresh]);
  // 브라우저 탭 제목에 대기 장수
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = data?.pendingSheets ? `(${data.pendingSheets}) ${base}` : base;
  }, [data?.pendingSheets]);
  return { data, refresh };
};

const AdminBell: React.FC<{ data: Notifications | null; refresh: () => void }> = ({ data, refresh }) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [toast, setToast] = useState<Notifications['unread'][number] | null>(null);
  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!data) return;
    const ids = new Set(data.unread.map((u) => u.batchId));
    if (seen.current) {
      const fresh = data.unread.find((u) => !seen.current!.has(u.batchId));
      if (fresh) setToast(fresh);
    }
    seen.current = ids;
  }, [data]);

  const open = (batchId?: string) => {
    setAnchor(null); setToast(null);
    if (batchId) api('/admin/cert/notifications/read', { body: { batchId } }).then(refresh).catch(() => undefined);
    navigate(`/admin/bt-teachers?tab=reports${batchId ? `&batch=${encodeURIComponent(batchId)}` : ''}`);
  };
  const readAll = () => api('/admin/cert/notifications/read', { body: {} }).then(refresh);
  const unread = data?.unread || [];

  return (
    <>
      <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)} aria-label="완성보고서 알림">
        <Badge badgeContent={unread.length} color="error" max={99}><NotificationsNone /></Badge>
      </IconButton>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box sx={{ width: 360, maxHeight: 480, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, borderBottom: `1px solid ${C.line}` }}>
            <Typography fontWeight={800} sx={{ flex: 1 }}>완성보고서 알림</Typography>
            {unread.length > 0 && <Button size="small" onClick={readAll}>모두 읽음</Button>}
          </Box>
          {!unread.length && <Typography sx={{ p: 3, textAlign: 'center', color: C.sub, fontSize: 14 }}>새 제출이 없습니다.</Typography>}
          {unread.map((u) => (
            <Box key={u.batchId} onClick={() => open(u.batchId)} sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${C.line}`, cursor: 'pointer', '&:hover': { bgcolor: '#fffbea' } }}>
              <Typography fontWeight={800} fontSize={14}>{u.churchName} <span style={{ color: C.sub, fontWeight: 500 }}>({u.regNo})</span> {u.count}장 {u.resubmit ? '재제출' : '제출'}</Typography>
              <Typography fontSize={12.5} color={C.sub}>담당 {u.managerName} · {mdhm(u.submittedAt)}{u.dateRequests ? <span style={{ color: C.warn }}> · 날짜요청 {u.dateRequests}</span> : null}</Typography>
            </Box>
          ))}
          <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography fontSize={12} color={C.sub}>처리 대기 {data?.pendingSheets || 0}장{data?.email.enabled ? ` · 📧 ${data.email.to}${data.email.configured ? '' : ' (SMTP 미설정)'}` : ''}</Typography>
            <Button size="small" onClick={() => open()}>승인 화면 ›</Button>
          </Box>
        </Box>
      </Popover>
      <Snackbar open={Boolean(toast)} onClose={(_, reason) => reason !== 'clickaway' && setToast(null)} autoHideDuration={12000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} TransitionComponent={Slide}>
        <Paper elevation={6} sx={{ p: 2, width: 320, borderLeft: '5px solid #f5b800' }}>
          <Typography fontSize={12} color={C.sub} fontWeight={700}>새 완성보고서</Typography>
          <Typography fontWeight={800}>{toast?.churchName} {toast?.count}장 {toast?.resubmit ? '재제출' : '제출'}</Typography>
          <Typography fontSize={12.5} color={C.sub}>담당 {toast?.managerName}{toast?.dateRequests ? ` · 날짜요청 ${toast.dateRequests}` : ''}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button size="small" onClick={() => setToast(null)}>닫기</Button>
            <Button size="small" variant="contained" onClick={() => open(toast?.batchId)}>확인하기</Button>
          </Box>
        </Paper>
      </Snackbar>
    </>
  );
};

export default AdminBell;
